import { useMemo, useState } from 'react'
import {
  SurveySchema,
  SurveyQuestion,
  SurveyResponse,
  collectSurveyHiddenFields,
  submitSurvey,
  markSurveyDone,
} from './survey'
import { track } from './analytics'

// Schema-driven end-of-play survey, rendered as a scrollable form screen (not an
// overlay — it's its own phase). Adapted from maffdoku's SurveyPage, light-themed
// for labs. All questions optional; Skip is always available.
export const SurveyScreen = ({
  schema,
  sessionId,
  gameType,
  onDone,
}: {
  schema: SurveySchema
  sessionId: string
  gameType?: string // 'labs-24' (default) | 'labs-cryptic' | 'labs-rebus' — stamped on the response
  onDone: () => void // called after submit+thank-you, or on skip
}) => {
  const [answers, setAnswers] = useState<Record<string, string | number | string[] | null>>({})
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const requiredUnanswered = useMemo(
    () =>
      schema.questions
        .filter((q) => q.required)
        .filter((q) => {
          const v = answers[q.id]
          if (v === null || v === undefined) return true
          if (typeof v === 'string' && v.trim() === '') return true
          if (Array.isArray(v) && v.length === 0) return true
          return false
        })
        .map((q) => q.id),
    [schema.questions, answers]
  )

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (submitting || submitted) return
    if (requiredUnanswered.length > 0) {
      setError('Please answer the highlighted questions before submitting.')
      document.getElementById('q-' + requiredUnanswered[0])?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      return
    }
    setError(null)
    setSubmitting(true)
    const response: SurveyResponse = {
      surveyId: schema.id,
      answers,
      hiddenFields: await collectSurveyHiddenFields(sessionId, gameType),
      submittedAt: new Date().toISOString(),
    }
    try {
      await submitSurvey(response)
      markSurveyDone(schema.id)
      track('labs_survey_submit')
      setSubmitted(true)
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.error('[survey] submission failed:', err)
      setError('Sorry — we couldn’t send that. Please try again.')
      setSubmitting(false)
    }
  }

  const handleSkip = () => {
    track('labs_survey_skip')
    markSurveyDone(schema.id)
    onDone()
  }

  if (submitted) {
    return (
      <div className="flex flex-col items-center px-6 pt-16 pb-10 text-center">
        <div className="text-4xl">🙏</div>
        <h2 className="mt-3 text-xl font-semibold text-gray-800">{schema.thankYouText}</h2>
        <button
          type="button"
          onClick={onDone}
          className="mt-6 w-full max-w-xs rounded-lg bg-[#820458] py-3 font-semibold text-white"
        >
          Continue
        </button>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="flex flex-col items-center px-6 pt-10 pb-10">
      <div className="w-full max-w-md">
        <h1 className="text-center text-xl font-semibold text-gray-800">{schema.title}</h1>
        {schema.intro && <p className="mt-2 text-center text-sm text-gray-500 leading-relaxed">{schema.intro}</p>}

        {schema.questions.map((q, i) => (
          <QuestionField
            key={q.id}
            question={q}
            index={i + 1}
            value={answers[q.id]}
            onChange={(v) => setAnswers((prev) => ({ ...prev, [q.id]: v }))}
            missing={requiredUnanswered.includes(q.id)}
          />
        ))}

        {error && <div className="mt-3 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div>}

        <button
          type="submit"
          disabled={submitting}
          className="mt-6 w-full rounded-lg bg-[#820458] py-3 font-semibold text-white disabled:opacity-60"
        >
          {submitting ? 'Sending…' : schema.submitText || 'Submit'}
        </button>
        <button type="button" onClick={handleSkip} className="mt-3 w-full text-center text-sm text-gray-400 underline">
          No thanks
        </button>
      </div>
    </form>
  )
}

function QuestionField({
  question,
  index,
  value,
  onChange,
  missing,
}: {
  question: SurveyQuestion
  index: number
  value: string | number | string[] | null | undefined
  onChange: (v: string | number | string[]) => void
  missing: boolean
}) {
  return (
    <>
      {question.sectionBefore && (
        <h2 className="mt-8 mb-2 border-t border-gray-100 pt-4 text-sm font-semibold text-[#820458]">
          {question.sectionBefore}
        </h2>
      )}
      <div id={'q-' + question.id} className={'mt-5 ' + (missing ? '-ml-3 border-l-4 border-rose-400 pl-3' : '')}>
        <label className="block text-sm font-medium text-gray-700">
          <span className="mr-1 text-gray-400">{index}.</span>
          {question.question}
          {question.required && <span className="ml-1 text-rose-500">*</span>}
        </label>

        {question.type === 'single-choice' && (
          <div className="mt-2 flex flex-col gap-1.5">
            {question.options.map((opt) => {
              const checked = value === opt
              return (
                <label
                  key={opt}
                  className={
                    'flex cursor-pointer items-center rounded-lg border px-3 py-2 text-sm transition-colors ' +
                    (checked ? 'border-[#820458] bg-[#820458] text-white' : 'border-gray-200 text-gray-700 hover:border-[#820458]')
                  }
                >
                  <input
                    type="radio"
                    name={question.id}
                    checked={checked}
                    onChange={() => onChange(opt)}
                    className="mr-2 accent-[#820458]"
                  />
                  {opt}
                </label>
              )
            })}
          </div>
        )}

        {question.type === 'multi-choice' && (
          <div className="mt-2 flex flex-col gap-1.5">
            {question.options.map((opt) => {
              const arr = Array.isArray(value) ? value : []
              const checked = arr.includes(opt)
              return (
                <label
                  key={opt}
                  className={
                    'flex cursor-pointer items-center rounded-lg border px-3 py-2 text-sm transition-colors ' +
                    (checked ? 'border-[#820458] bg-[#820458] text-white' : 'border-gray-200 text-gray-700 hover:border-[#820458]')
                  }
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => onChange(checked ? arr.filter((v) => v !== opt) : [...arr, opt])}
                    className="mr-2 accent-[#820458]"
                  />
                  {opt}
                </label>
              )
            })}
          </div>
        )}

        {question.type === 'scale' && (
          <div className="mt-2">
            <div
              className="grid gap-1"
              style={{ gridTemplateColumns: `repeat(${question.max - question.min + 1}, minmax(0, 1fr))` }}
            >
              {Array.from({ length: question.max - question.min + 1 }, (_, i) => {
                const n = question.min + i
                const checked = value === n
                return (
                  <button
                    key={n}
                    type="button"
                    onClick={() => onChange(n)}
                    aria-pressed={checked}
                    className={
                      'rounded-lg border py-2 text-sm font-semibold transition-colors ' +
                      (checked ? 'border-[#820458] bg-[#820458] text-white' : 'border-gray-200 text-gray-600 hover:border-[#820458]')
                    }
                  >
                    {n}
                  </button>
                )
              })}
            </div>
            {(question.minLabel || question.maxLabel) && (
              <div className="mt-1 flex justify-between px-0.5 text-xs text-gray-400">
                <span>{question.minLabel || ''}</span>
                <span>{question.maxLabel || ''}</span>
              </div>
            )}
          </div>
        )}

        {question.type === 'text' &&
          (question.multiline ? (
            <textarea
              value={typeof value === 'string' ? value : ''}
              onChange={(e) => onChange(e.target.value)}
              placeholder={question.placeholder}
              rows={3}
              className="mt-2 w-full resize-none rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-[#820458] focus:outline-none"
            />
          ) : (
            <input
              type="text"
              value={typeof value === 'string' ? value : ''}
              onChange={(e) => onChange(e.target.value)}
              placeholder={question.placeholder}
              className="mt-2 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-[#820458] focus:outline-none"
            />
          ))}
      </div>
    </>
  )
}
